import {useEffect} from 'react'
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom'

import {getSingleBuildArg} from '../../../../../../rest/builds'
import {getBuildLocator} from '../../../../../../rest/locators'
import {restApi} from '../../../../../../services/rest'
import {subscribeOnBuildTypeEvents} from '../../../../../../utils/subscriber'
import {
  BUILD_FINISHED,
  BUILD_INTERRUPTED,
  BUILD_STARTED,
  BUILD_TYPE_REMOVED_FROM_QUEUE,
} from '../../../../../../utils/subscriptionEvents'
import {getTabParamsKey} from '../../../../../../utils/tabs'
import {usePipelineRunArg} from '../../PipelineRunPage.hooks'

function useJobDetails(jobId: number) {
  const [searchParams] = useSearchParams()

  const arg = usePipelineRunArg()
  const {isPipelineFinished} = restApi.endpoints.getBuildNormalized.useQuery(arg, {
    selectFromResult: ({data}) => ({
      isPipelineFinished: data?.entities?.builds?.[data?.result!]?.state === 'finished',
    }),
  })

  const {
    areTabsLoading,
    tabs,
    refetch: refetchTabs,
  } = restApi.endpoints.getTabs.useQuery(
    {key: getTabParamsKey({buildId: jobId}), cacheTabs: true},
    {
      selectFromResult: ({isLoading, data}) => ({areTabsLoading: isLoading, tabs: data}),
    },
  )
  const location = useLocation()
  const navigate = useNavigate()
  const {headInternalId, isRunLoading, refetch} = restApi.endpoints.getBuildNormalized.useQuery(
    getSingleBuildArg(getBuildLocator(jobId), {
      withBuildTypeDetails: true,
    }),
    {
      selectFromResult: ({data, isLoading}) => {
        const run = data?.entities?.builds?.[data?.result!]
        const headId = run?.buildType
        return {
          headInternalId: data?.entities?.buildTypes?.[headId!]?.internalId,
          isRunLoading: isLoading,
        }
      },
    },
  )

  useEffect(
    () =>
      headInternalId
        ? subscribeOnBuildTypeEvents(
            headInternalId,
            [BUILD_TYPE_REMOVED_FROM_QUEUE, BUILD_STARTED, BUILD_FINISHED, BUILD_INTERRUPTED],
            () => {
              refetchTabs()
              refetch()
            },
          )
        : undefined,
    [headInternalId, refetch, refetchTabs],
  )

  return {
    searchParams,
    navigate,
    location,
    isPipelineFinished,
    isLoading: areTabsLoading || isRunLoading,
    tabs,
  }
}

export default useJobDetails
